import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FlaskConical, ShieldCheck, FileText, Globe } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import TestimonialsBlock from '../components/TestimonialsBlock';
import { getProducts } from '../lib/api';
import logger from '../lib/logger';

const HomePage = () => {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProducts()
      .then((data) => {
        setFeatured(data.filter((p) => p.tags?.includes('featured')).slice(0, 12));
      })
      .catch((e) => logger.error('Failed to load products', e))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-black text-white">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(circle at 75% 50%, rgba(144,144,144,0.18) 0%, transparent 55%)',
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-4 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="font-bold text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight">
              Hydro Formula Quality{' '}
              <span className="text-[#909090]">—</span>
              <br />
              <span className="text-[#909090]">More Than Purity</span>
            </h1>
            <p className="mt-6 text-zinc-300 text-lg leading-relaxed max-w-xl">
              At Hydro Health, quality goes far beyond purity. Every batch is
              independently verified using a research protocol that surpasses
              standard industry testing.
            </p>
            <p className="mt-4 text-zinc-400 text-sm leading-relaxed max-w-xl">
              No shortcuts. No surface-level testing. No blind trust in purity alone.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 bg-[#909090] hover:bg-[#a8a8a8] text-black font-semibold px-7 py-3.5 rounded transition-colors"
              >
                Shop Now <ArrowRight size={18} />
              </Link>
              <Link
                to="/coas"
                className="inline-flex items-center gap-2 border border-zinc-700 hover:border-[#909090] hover:text-[#909090] text-white font-medium px-7 py-3.5 rounded transition-colors"
              >
                View Lab COAs
              </Link>
            </div>
            <p className="mt-8 text-xs text-white font-semibold uppercase tracking-wide max-w-md">
              NOT FOR RESALE OR HUMAN CONSUMPTION &mdash; RESEARCH PURPOSES ONLY
            </p>
          </div>

          <div className="relative flex justify-center">
            <div className="absolute -inset-10 bg-[#909090]/10 rounded-full blur-3xl" />
            <img
              src="/products/platinum-vial-hero.png"
              alt="Hydro Health research vial"
              className="relative max-h-[520px] object-contain drop-shadow-[0_25px_45px_rgba(144,144,144,0.25)]"
            />
          </div>
        </div>
      </section>

      <section className="border-y border-zinc-900 bg-zinc-950/40">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: FlaskConical, title: 'Independently Tested', text: 'Third-party Liquilabs analysis' },
            { icon: ShieldCheck, title: '≥99% Purity', text: 'Verified per batch' },
            { icon: FileText, title: 'COA Included', text: 'Available for every product' },
            { icon: Globe, title: 'Worldwide Shipping', text: 'Discreet & tracked' },
          ].map((item) => (
            <div key={item.title} className="flex items-start gap-3">
              <item.icon className="text-[#909090] shrink-0" size={28} />
              <div>
                <p className="text-white font-semibold text-sm">{item.title}</p>
                <p className="text-zinc-400 text-xs mt-0.5">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-[#909090] text-sm uppercase tracking-widest mb-2">
              Lab-tested research compounds
            </p>
            <h2 className="text-3xl md:text-4xl font-bold">Our Products</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 text-[#909090] hover:text-[#a8a8a8] text-sm font-medium">
            View all <ArrowRight size={16} />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={`skel-${i}`} className="aspect-square bg-zinc-950 border border-zinc-900 rounded animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}

        <div className="mt-12 text-center">
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 bg-[#909090] hover:bg-[#a8a8a8] text-black font-semibold px-8 py-3.5 rounded transition-colors"
          >
            Shop Now <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <section className="border-t border-zinc-900 bg-gradient-to-b from-zinc-950 to-black">
        <div className="max-w-[1100px] mx-auto px-4 lg:px-8 py-20 text-center">
          <p className="text-[#909090] text-sm uppercase tracking-widest mb-3">
            About Hydro Health
          </p>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Quality without shortcuts.
          </h2>
          <p className="text-zinc-300 text-lg leading-relaxed max-w-3xl mx-auto">
            At Hydro Health, we work with our own brand &mdash; Hydro Formula. Driven by
            one belief: research-grade quality, designed for laboratories that
            require verified, reliable and transparently documented materials.
          </p>
          <Link
            to="/about"
            className="inline-flex mt-8 items-center gap-2 border border-[#909090] text-[#909090] hover:bg-[#909090] hover:text-black font-semibold px-7 py-3 rounded transition-colors"
          >
            Read more <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[#909090] text-sm uppercase tracking-widest mb-3">
              Extended Analytical Panel
            </p>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              More than just a purity number.
            </h2>
            <ul className="space-y-3 text-zinc-300">
              {[
                'Identity & purity verification',
                'Heavy metals screening',
                'Endotoxin testing',
                'Microbial safety analysis',
                'Structural integrity assessment',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#909090] mt-2.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-[#909090]/10 rounded-full blur-3xl" />
            <img
              src="/products/platinum-vial-hero.png"
              alt="Hydro Health research vial"
              className="relative mx-auto max-h-[420px] object-contain"
            />
          </div>
        </div>
      </section>

      <TestimonialsBlock />
    </div>
  );
};

export default HomePage;
